import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  lazy,
} from "react";
import {
  View,
  StyleSheet,
  FlatList,
  Text,
  Modal,
  SafeAreaView,
  TextInput,
  TouchableOpacity,
  Dimensions,
  Animated,
  Easing,
  Platform,
  ActivityIndicator,
  Pressable,
  Linking,
  InteractionManager,
} from "react-native";
import {
  trackOpportunityCardClick,
  trackFilterUse,
  trackSearch,
  trackViewChange,
  trackPageView,
} from "../utils/analyticsUtils";
import { StatusBar } from "expo-status-bar";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import Header from "../components/Header";
import OpportunityCard from "../components/OpportunityCard";
import ReportIssueForm from "../components/ReportIssueForm";
import CalendarView from "../components/CalendarView";
import FeaturedOpportunity from "../components/FeaturedOpportunity";
import FeedbackModal from "../components/FeedbackModal";
import ActiveFilters from "../components/ActiveFilters";
import AdminPanel from "../components/admin/AdminPanel";
import SearchBar from "../components/SearchBar";
import AuthRequiredModal from "../components/auth/AuthRequiredModal";
import MilestoneBanner from "../components/MilestoneBanner";
// Team page temporarily hidden
// import TeamBanner from "../components/TeamBanner";
import Footer from "../components/Footer";
import { useAuth } from "../contexts/AuthContext";
import { useFavorites } from "../contexts/FavoritesContext";
import {
  Opportunity,
  FilterOptions as FilterOptionsType,
  OpportunityType,
  OpportunityStatus,
  GradeLevel,
  InteractionType,
} from "../types";
import {
  filterOpportunities,
  calculateOpportunityStatus,
} from "../utils/filterUtils";
import {
  getOpportunities,
  getFeaturedOpportunityId,
  getOpportunityById,
} from "../services/parseService";
import { performSemanticSearch, trackSemanticSearch } from "../services/aiSearchService";
import { useTheme } from "../contexts/ThemeContext";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  updateDocumentTitle,
  updateMetaTag,
  updateCanonicalLink,
  updateStructuredData,
  updateOpportunitySEO,
  SITE_CONFIG,
} from "../utils/seoUtils";

// View mode types - updated to include saved view
type ViewMode = "grid" | "calendar" | "saved";

const { width, height } = Dimensions.get("window");
const isTabletOrDesktop = width >= 768;
const isSmallMobile = width < 380;
const numColumns = width > 1200 ? 3 : width > 800 ? 2 : 1;

// Remove hardcoded featured opportunity ID
// const FEATURED_OPPORTUNITY_ID = "HlFEuPb5Qt"; // Example ID - change this to the desired opportunity ID

const opportunityTypes: OpportunityType[] = [
  "Competition",
  "Grant",
  "Scholarship",
  "Hackathon",
  "Internship",
  "Workshop/Programs",
  "Conference",
  "Other",
];

const gradeLevels: GradeLevel[] = [
  "Elementary",
  "High School",
  "Undergraduate",
  "Graduate",
];

const interactionTypes: InteractionType[] = [
  "In-Person",
  "Virtual",
  "Hybrid",
];

// Lazy import OpportunityDetail for code splitting
const OpportunityDetail = lazy(() => import("../components/OpportunityDetail"));

const HomeScreen: React.FC<{ navigation: any; route?: any }> = ({ navigation, route }) => {
  const { theme } = useTheme();
  const { isAuthenticated } = useAuth();
  const { savedOpportunities, isLoading: isSavedLoading, refresh: refreshSaved } = useFavorites();

  // Themed colors - memoized for performance
  const themed = useMemo(
    () => ({
      background: theme === "dark" ? "#18181B" : "#FAFAFA",
      card: theme === "dark" ? "#232327" : "#FFFFFF",
      text: theme === "dark" ? "#F2F2F2" : "#18181B",
      subText: theme === "dark" ? "#C2C2C2" : "#52525B",
      inputBg: theme === "dark" ? "#232327" : "#F4F4F5",
      inputBorder: theme === "dark" ? "#34343B" : "#E4E4E7",
      inputText: theme === "dark" ? "#F2F2F2" : "#27272A",
      chip: theme === "dark" ? "#232327" : "#F4F4F5",
      chipActive: theme === "dark" ? "#F2F2F2" : "#272727",
      chipText: theme === "dark" ? "#C2C2C2" : "#52525B",
      chipActiveText: theme === "dark" ? "#232327" : "#FFFFFF",
      filterPanel: theme === "dark" ? "#232327" : "#FFFFFF",
      filterPanelBorder: theme === "dark" ? "#34343B" : "#E4E4E7",
      resultBar: theme === "dark" ? "#232327" : "#F9F9F9",
      resultBarBorder: theme === "dark" ? "#34343B" : "#EEEEEE",
      emptyText: theme === "dark" ? "#C2C2C2" : "#3F3F46",
      emptySubText: theme === "dark" ? "#888" : "#71717A",
      modalBg: theme === "dark" ? "rgba(20,20,20,0.8)" : "rgba(0,0,0,0.5)",
      modalCard: theme === "dark" ? "#22222A" : "#fff",
      adminCard: theme === "dark" ? "#202024" : "#fff",
      footerBg: theme === "dark" ? "#18181B" : "#F7FAFC",
      footerText: theme === "dark" ? "#A0AEC0" : "#718096",
      iconInactive: theme === "dark" ? "#71717A" : "#71717A",
      iconActive: theme === "dark" ? "#FFFFFF" : "#272727",
      filterButton: theme === "dark" ? "#232327" : "#F4F4F5",
      filterButtonActive: theme === "dark" ? "#34343B" : "#E4E4E7",
      filterButtonBorder: theme === "dark" ? "#34343B" : "#E4E4E7",
      retryButton: theme === "dark" ? "#F2F2F2" : "#272727",
      retryText: theme === "dark" ? "#232327" : "#FFFFFF",
      progressBar: theme === "dark" ? "#F2F2F2" : "#000000",
      progressGlow: theme === "dark" ? "#232327" : "#FFFFFF",
      reportButton: theme === "dark" ? "#272727" : "#F2F2F2",
      reportIcon: theme === "dark" ? "#FFFFFF" : "#232327",
      underConstructionBg: theme === "dark" ? "#202024" : "#F9FAFB",
      underConstructionCard: theme === "dark" ? "#232327" : "#FFFFFF",
      underConstructionCardBorder: theme === "dark" ? "#34343B" : "#E5E5E5",
      underConstructionTitle: theme === "dark" ? "#F2F2F2" : "#000000",
      underConstructionText: theme === "dark" ? "#C2C2C2" : "#4A5568",
      returnToGridButton: theme === "dark" ? "#F2F2F2" : "#000000",
      returnToGridText: theme === "dark" ? "#232327" : "#FFFFFF",
      footerLogo: theme === "dark" ? "#F2F2F2" : "#272727",
      socialIcon: theme === "dark" ? "#232327" : "#EDF2F7",
      socialIconColor: theme === "dark" ? "#C2C2C2" : "#555555",
      borderTopColor: theme === "dark" ? "#232327" : "#E2E8F0",
      canadaDayBg: theme === "dark" ? "#232327" : "#FEF2F2",
      canadaDayBorder: theme === "dark" ? "#34343B" : "#FECACA",
    }),
    [theme],
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [filters, setFilters] = useState<FilterOptionsType>({});
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const initialOpportunities: Opportunity[] =
    typeof window !== 'undefined' && Array.isArray((window as any).__INITIAL_DATA__)
      ? (window as any).__INITIAL_DATA__
      : [];
  const [allOpportunities, setAllOpportunities] = useState<Opportunity[]>(initialOpportunities);
  const [filteredOpportunities, setFilteredOpportunities] = useState<
    Opportunity[]
  >(initialOpportunities);
  const [selectedOpportunity, setSelectedOpportunity] =
    useState<Opportunity | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<OpportunityType[]>([]);
  const [selectedGradeLevels, setSelectedGradeLevels] = useState<GradeLevel[]>(
    [],
  );
  const [selectedStatuses, setSelectedStatuses] = useState<OpportunityStatus[]>(
    [],
  );
  const [selectedInteractionTypes, setSelectedInteractionTypes] = useState<InteractionType[]>(
    [],
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(initialOpportunities.length === 0);
  const [isAISearchEnabled, setIsAISearchEnabled] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isReportFormVisible, setIsReportFormVisible] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [featuredOpportunityId, setFeaturedOpportunityId] = useState<string | null>(null);
  const [showAuthRequiredModal, setShowAuthRequiredModal] = useState(false);
  const [showCanadaDayBanner, setShowCanadaDayBanner] = useState(true);
  const [showMilestoneBanner, setShowMilestoneBanner] = useState(true);
  const [showTeamBanner, setShowTeamBanner] = useState(true);

  // Animation references
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const detailSlideAnim = useRef(new Animated.Value(height)).current;
  const backdropOpacity = useRef(new Animated.Value(0)).current;
  const progressAnim = useRef(new Animated.Value(0)).current;
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const shimmerAnim = useRef(new Animated.Value(0)).current;
  const scrollY = useRef(new Animated.Value(0)).current;

  const headerOpacity = scrollY.interpolate({
    inputRange: [0, 100],
    outputRange: [0, 1],
    extrapolate: "clamp",
  });

  const mainScrollViewRef = useRef<any>(null);

  // Track page view and setup when HomeScreen loads  
  useEffect(() => {
    trackPageView("Home", "/");

    // Set up SEO from the URL the page was loaded with. /opportunity/:id is a
    // deep link rendered by HomeScreen (React Navigation collapses it to "/"),
    // so we key off the real pathname at mount and apply that opportunity's SEO
    // from the static __INITIAL_DATA__. This keeps the per-opportunity
    // canonical/title/description for JS-rendering crawlers (Googlebot) instead
    // of letting setupHomeSEO reset them to the homepage. The deep-link effect
    // below re-asserts it once navigation/data settle.
    if (Platform.OS === "web") {
      const oppMatch = window.location.pathname.match(/^\/opportunity\/([^/]+)$/);
      const seed: any[] = Array.isArray((window as any).__INITIAL_DATA__)
        ? (window as any).__INITIAL_DATA__
        : [];
      const deepOpp = oppMatch
        ? seed.find((o: any) => o.id === oppMatch[1]) ||
          (seed.length === 1 ? seed[0] : null)
        : null;
      if (deepOpp) {
        try {
          updateOpportunitySEO(deepOpp);
          // Own document.title through React Navigation so its native-stack web
          // title sync carries the per-opportunity title instead of resetting
          // it back to the Home screen's default.
          navigation.setOptions?.({
            title: `${deepOpp.title} | ${SITE_CONFIG.siteName}`,
          });
        } catch (e) {
          console.warn("Failed to update opportunity SEO", e);
        }
      } else {
        setupHomeSEO();
      }
    }

    // Load featured opportunity ID
    const loadFeaturedOpportunityId = async () => {
      try {
        const featuredId = await getFeaturedOpportunityId();
        setFeaturedOpportunityId(featuredId);
      } catch (error) {
        console.error("Error loading featured opportunity ID:", error);
      }
    };

    // Check if milestone banner was dismissed
    const checkMilestoneBannerStatus = async () => {
      try {
        const dismissed = await AsyncStorage.getItem("milestoneBannerDismissed");
        if (dismissed === "true") {
          setShowMilestoneBanner(false);
        }
      } catch (error) {
        console.error("Error checking milestone banner status:", error);
      }
    };

    // Check if team banner was dismissed
    const checkTeamBannerStatus = async () => {
      try {
        const dismissed = await AsyncStorage.getItem("teamBannerDismissed");
        if (dismissed === "true") {
          setShowTeamBanner(false);
        }
      } catch (error) {
        console.error("Error checking team banner status:", error);
      }
    };

    loadFeaturedOpportunityId();
    checkMilestoneBannerStatus();
    checkTeamBannerStatus();
  }, []);

  // Debounce search query for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    // Load opportunities from Back4app with InteractionManager for better performance
    const loadOpportunities = async () => {
      try {
        if (initialOpportunities.length === 0) setIsLoading(true);
        setLoadError(null);

        // Fetch data first - increased limit to get all opportunities
        console.log('📊 Loading opportunities from database...');
        const remoteOpportunities = await getOpportunities(500);
        console.log(`📊 Fetched ${remoteOpportunities.length} opportunities from database`);

        // Defer heavy processing to after interactions complete
        InteractionManager.runAfterInteractions(() => {
          try {
            // Parse and transform data in batches for better performance
            const parsedOpportunities = remoteOpportunities.map((opp: any) => {
              if (opp.attributes) {
                const id = opp.id;
                const attributes = { ...opp.attributes };
                const parseDate = (dateString: string | Date) => {
                  if (dateString instanceof Date)
                    return dateString.toISOString();
                  return dateString;
                };
                return {
                  id,
                  ...attributes,
                  startDate: attributes.startDate
                    ? parseDate(attributes.startDate)
                    : null,
                  endDate: attributes.endDate
                    ? parseDate(attributes.endDate)
                    : null,
                  deadline: attributes.deadline
                    ? parseDate(attributes.deadline)
                    : null,
                  applicationOpenDate: attributes.applicationOpenDate
                    ? parseDate(attributes.applicationOpenDate)
                    : null,
                  applicationCloseDate: attributes.applicationCloseDate
                    ? parseDate(attributes.applicationCloseDate)
                    : null,
                };
              }
              return opp;
            });

            // Calculate status for all opportunities
            const opportunitiesWithStatus = parsedOpportunities.map(
              (opp: any) => ({
                ...opp,
                status: calculateOpportunityStatus(opp),
              }),
            );

            // Sort opportunities
            const statusPriority: Record<OpportunityStatus, number> = {
              Open: 1,
              "Coming Soon": 2,
              Ongoing: 3,
              Closed: 4,
            };

            const sortedOpportunities = opportunitiesWithStatus.sort((a: any, b: any) => {
              const statusA = a.status as OpportunityStatus;
              const statusB = b.status as OpportunityStatus;
              const statusOrderA = statusPriority[statusA] || 5;
              const statusOrderB = statusPriority[statusB] || 5;
              if (statusOrderA !== statusOrderB) {
                return statusOrderA - statusOrderB;
              }
              const createdAtA = new Date(a.createdAt || 0).getTime();
              const createdAtB = new Date(b.createdAt || 0).getTime();
              return createdAtA - createdAtB;
            });

            // Update state after all processing is complete
            setAllOpportunities(sortedOpportunities);
            setFilteredOpportunities(sortedOpportunities);

            // Note: AI embeddings are managed through Admin Panel only
            // Deep-link opportunity opening is handled in a dedicated effect below.

            setIsLoading(false);
          } catch (error) {
            console.error("Error in processing:", error);
            // Keep the build-time snapshot if we have one (e.g. crawlers /
            // offline) rather than blanking the page with an error.
            if (initialOpportunities.length === 0) {
              setLoadError(
                "Failed to process opportunities. Please try again later.",
              );
            }
            setIsLoading(false);
          }
        });
      } catch (error) {
        console.error("Error loading opportunities:", error);
        // The build-time snapshot (window.__INITIAL_DATA__) is the resilient
        // fallback when the live Parse fetch fails — most importantly for
        // Googlebot's renderer, whose request to Back4App often fails. Only
        // surface an error / clear the grid when there is no snapshot to show,
        // otherwise the seeded opportunities stay visible and the page is not a
        // soft 404.
        if (initialOpportunities.length === 0) {
          setLoadError("Failed to load opportunities. Please try again later.");
          setAllOpportunities([]);
          setFilteredOpportunities([]);
        }
        setIsLoading(false);
      }
    };

    loadOpportunities();
  }, [navigation]);

  // Open a specific opportunity from a shared/deep link (e.g. /opportunity/:id).
  // Runs whenever the route param or the loaded opportunities change, so it works
  // whether the param is present on mount or arrives later via navigate().
  useEffect(() => {
    const opportunityId = route?.params?.opportunityId;
    if (!opportunityId || allOpportunities.length === 0) return;

    const opportunity = allOpportunities.find(
      (opp: any) => opp.id === opportunityId,
    );
    if (opportunity) {
      setSelectedOpportunity(opportunity);
      showDetailModal();
      // Keep the rendered SEO pointed at this opportunity so JS-rendering
      // crawlers (Googlebot) index /opportunity/:id with its own canonical and
      // title instead of the homepage values setupHomeSEO would otherwise apply.
      if (Platform.OS === "web") {
        try {
          updateOpportunitySEO(opportunity);
          navigation.setOptions?.({
            title: `${opportunity.title} | ${SITE_CONFIG.siteName}`,
          });
        } catch (e) {
          console.warn("Failed to update opportunity SEO", e);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route?.params?.opportunityId, allOpportunities]);

  // Helper function to add featured opportunity to results
  const addFeaturedOpportunityToResults = useCallback((results: Opportunity[]): Opportunity[] => {
    if (!featuredOpportunityId) {
      return results;
    }

    // Find the featured opportunity in all opportunities
    const featuredOpp = allOpportunities.find(
      (opp) => opp.id === featuredOpportunityId,
    );

    if (!featuredOpp) {
      return results;
    }

    // Check if the featured opportunity is already in the filtered results
    const featuredOppInResults = results.find(
      (opp) => opp.id === featuredOpportunityId,
    );

    if (featuredOppInResults) {
      // Remove the regular version and add the featured version at the beginning
      const filteredResults = results.filter(
        (opp) => opp.id !== featuredOpportunityId,
      );
      return [{
        ...featuredOpp,
        isFeatured: true,
      } as Opportunity & { isFeatured: boolean }, ...filteredResults];
    }

    // Featured opportunity doesn't match current filters, so don't include it
    return results;
  }, [featuredOpportunityId, allOpportunities]);

  // Manual search function (triggered by Enter or search button)
  const performSearch = async (query: string = searchQuery) => {
    console.log('🚀 ===== SEARCH TRIGGERED =====');
    console.log('🚀 Query:', query);
    console.log('🚀 AI Search Enabled:', isAISearchEnabled);
    console.log('🚀 View Mode:', viewMode);
    console.log('🚀 Total opportunities available:', viewMode === "saved" ? savedOpportunities.length : allOpportunities.length);
    console.log('🚀 Active filters:', {
      types: selectedTypes,
      grades: selectedGradeLevels,
      statuses: selectedStatuses,
      interactions: selectedInteractionTypes
    });

    let results: Opportunity[] = [];
    const opportunitiesToSearch = viewMode === "saved" ? savedOpportunities : allOpportunities;

    if (isAISearchEnabled && query.trim()) {
      console.log('🤖 Using AI SEMANTIC SEARCH');
      console.log('🤖 Starting semantic search...');

      // Use AI semantic search
      const semanticStartTime = Date.now();
      const semanticResults = await performSemanticSearch(query, opportunitiesToSearch);
      const semanticTime = Date.now() - semanticStartTime;

      console.log('🤖 Semantic search completed in', semanticTime, 'ms');
      console.log('🤖 Semantic results count:', semanticResults.length);

      // Track the search
      await trackSemanticSearch(query, semanticResults.length);

      // Apply other filters on semantic search results
      const newFilters: FilterOptionsType = {
        type: selectedTypes.length > 0 ? selectedTypes : undefined,
        gradeLevel: selectedGradeLevels.length > 0 ? selectedGradeLevels : undefined,
        status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        interactionType: selectedInteractionTypes.length > 0 ? selectedInteractionTypes : undefined,
      };

      console.log('🔧 Applying additional filters to semantic results:', newFilters);
      results = filterOpportunities(semanticResults, newFilters);
      console.log('🔧 Results after filtering:', results.length);

    } else {
      console.log('🔍 Using REGULAR KEYWORD SEARCH');
      console.log('🔍 Reason:', !isAISearchEnabled ? 'AI search disabled' : 'Empty query');

      // Use regular keyword search
      const newFilters: FilterOptionsType = {
        ...filters,
        search: query,
        type: selectedTypes.length > 0 ? selectedTypes : undefined,
        gradeLevel: selectedGradeLevels.length > 0 ? selectedGradeLevels : undefined,
        status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        interactionType: selectedInteractionTypes.length > 0 ? selectedInteractionTypes : undefined,
      };

      console.log('🔍 Applied filters:', newFilters);
      setFilters(newFilters);
      results = filterOpportunities(opportunitiesToSearch, newFilters);
      console.log('🔍 Keyword search results:', results.length);
    }

    // Add featured opportunity if it matches the current filters/search (only for non-saved view)
    const finalResults = viewMode === "saved" ? results : addFeaturedOpportunityToResults(results);

    console.log('🎯 FINAL SEARCH RESULTS:');
    console.log('🎯 Total final results:', finalResults.length);
    console.log('🎯 Featured opportunity included:', finalResults.some(r => (r as any).isFeatured));
    console.log('🎯 First 3 result titles:', finalResults.slice(0, 3).map(r => r.title));
    console.log('🚀 ===== SEARCH COMPLETED =====');

    setFilteredOpportunities(finalResults);
  };

  // Apply filters when non-search filters change (immediate)
  useEffect(() => {
    if (viewMode === "saved") {
      // For saved view, filter the saved opportunities
      if (!isAISearchEnabled || !debouncedSearchQuery.trim()) {
        const newFilters: FilterOptionsType = {
          search: debouncedSearchQuery,
          type: selectedTypes.length > 0 ? selectedTypes : undefined,
          gradeLevel: selectedGradeLevels.length > 0 ? selectedGradeLevels : undefined,
          status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
          interactionType: selectedInteractionTypes.length > 0 ? selectedInteractionTypes : undefined,
        };

        const results = filterOpportunities(savedOpportunities, newFilters);
        setFilteredOpportunities(results);
      }
    } else if (!isAISearchEnabled) {
      // For regular search, apply filters immediately
      const newFilters: FilterOptionsType = {
        ...filters,
        search: debouncedSearchQuery,
        type: selectedTypes.length > 0 ? selectedTypes : undefined,
        gradeLevel: selectedGradeLevels.length > 0 ? selectedGradeLevels : undefined,
        status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        interactionType: selectedInteractionTypes.length > 0 ? selectedInteractionTypes : undefined,
      };

      setFilters(newFilters);
      const results = filterOpportunities(allOpportunities, newFilters);
      const finalResults = addFeaturedOpportunityToResults(results);
      setFilteredOpportunities(finalResults);
    } else if (!debouncedSearchQuery.trim()) {
      // If AI search is enabled but no query, show filtered results without AI
      const newFilters: FilterOptionsType = {
        type: selectedTypes.length > 0 ? selectedTypes : undefined,
        gradeLevel: selectedGradeLevels.length > 0 ? selectedGradeLevels : undefined,
        status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        interactionType: selectedInteractionTypes.length > 0 ? selectedInteractionTypes : undefined,
      };

      const results = filterOpportunities((viewMode as ViewMode) === "saved" ? savedOpportunities : allOpportunities, newFilters);
      const finalResults = (viewMode as ViewMode) === "saved" ? results : addFeaturedOpportunityToResults(results);
      setFilteredOpportunities(finalResults);
    }
  }, [
    debouncedSearchQuery,
    selectedTypes,
    selectedGradeLevels,
    selectedStatuses,
    selectedInteractionTypes,
    allOpportunities,
    savedOpportunities,
    isAISearchEnabled,
    featuredOpportunityId,
    addFeaturedOpportunityToResults,
    viewMode,
    filters.sortBy,
  ]);

  useEffect(() => {
    // Store animation references for proper cleanup
    const animations: Animated.CompositeAnimation[] = [];
    const animationValues = [fadeAnim, progressAnim, pulseAnim, shimmerAnim];

    // Create smoother, more sophisticated animations
    const rotateAnimation = Animated.loop(
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 4000, // Slower rotation
        useNativeDriver: true,
        easing: Easing.linear,
      }),
    );
    animations.push(rotateAnimation);

    // Progress bar animation - SIMPLIFIED
    const progressAnimation = Animated.loop(
      Animated.sequence([
        Animated.timing(progressAnim, {
          toValue: 0.7,
          duration: 2000, // Slower
          useNativeDriver: false,
          easing: Easing.inOut(Easing.ease),
        }),
        Animated.timing(progressAnim, {
          toValue: 0.3,
          duration: 1500,
          useNativeDriver: false,
          easing: Easing.inOut(Easing.ease),
        }),
        Animated.timing(progressAnim, {
          toValue: 0.9,
          duration: 2500,
          useNativeDriver: false,
          easing: Easing.inOut(Easing.ease),
        }),
      ]),
    );
    animations.push(progressAnimation);

    // Pulse animation for the icon - make it more subtle
    const pulseAnimation = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.05, // Reduce from 1.1 to 1.05 for subtler effect
          duration: 2000, // Even slower animation
          useNativeDriver: true,
          easing: Easing.inOut(Easing.ease),
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 2000, // Even slower animation
          useNativeDriver: true,
          easing: Easing.inOut(Easing.ease),
        }),
      ]),
    );
    animations.push(pulseAnimation);

    // Defer animations to after interaction for better performance
    InteractionManager.runAfterInteractions(() => {
      animations.forEach((animation) => animation.start());
    });

    // Comprehensive animation cleanup
    return () => {
      // Stop all running animations
      animations.forEach((animation) => {
        try {
          animation.stop();
        } catch (error) {
          console.warn("Animation cleanup error:", error);
        }
      });

      // Reset all animation values to prevent memory leaks
      animationValues.forEach((animValue) => {
        try {
          animValue.stopAnimation();
          animValue.removeAllListeners();
        } catch (error) {
          console.warn("Animation value cleanup error:", error);
        }
      });
    };
  }, [fadeAnim, progressAnim, pulseAnim, shimmerAnim]);

  const toggleFilterType = useCallback(
    (type: OpportunityType) => {
      if (selectedTypes.includes(type)) {
        setSelectedTypes(selectedTypes.filter((t) => t !== type));
      } else {
        setSelectedTypes([...selectedTypes, type]);
      }
    },
    [selectedTypes],
  );

  const toggleGradeLevel = useCallback(
    (level: GradeLevel) => {
      if (selectedGradeLevels.includes(level)) {
        setSelectedGradeLevels(selectedGradeLevels.filter((l) => l !== level));
      } else {
        setSelectedGradeLevels([...selectedGradeLevels, level]);
      }
    },
    [selectedGradeLevels],
  );

  const toggleStatusFilter = useCallback(
    (status: OpportunityStatus) => {
      if (selectedStatuses.includes(status)) {
        setSelectedStatuses(selectedStatuses.filter((s) => s !== status));
      } else {
        setSelectedStatuses([...selectedStatuses, status]);
      }
    },
    [selectedStatuses],
  );

  const toggleInteractionTypeFilter = useCallback(
    (type: InteractionType) => {
      if (selectedInteractionTypes.includes(type)) {
        setSelectedInteractionTypes(selectedInteractionTypes.filter((t) => t !== type));
      } else {
        setSelectedInteractionTypes([...selectedInteractionTypes, type]);
      }
    },
    [selectedInteractionTypes],
  );

  const removeTypeFilter = useCallback(
    (type: OpportunityType) => {
      setSelectedTypes(selectedTypes.filter((t) => t !== type));
    },
    [selectedTypes],
  );

  const removeStatusFilter = useCallback(
    (status: OpportunityStatus) => {
      setSelectedStatuses(selectedStatuses.filter((s) => s !== status));
    },
    [selectedStatuses],
  );

  const removeGradeLevelFilter = useCallback(
    (level: GradeLevel) => {
      setSelectedGradeLevels(selectedGradeLevels.filter((l) => l !== level));
    },
    [selectedGradeLevels],
  );

  const removeInteractionTypeFilter = useCallback(
    (type: InteractionType) => {
      setSelectedInteractionTypes(selectedInteractionTypes.filter((t) => t !== type));
    },
    [selectedInteractionTypes],
  );

  const clearFilters = useCallback(() => {
    setSelectedTypes([]);
    setSelectedGradeLevels([]);
    setSelectedStatuses([]);
    setSelectedInteractionTypes([]);
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setFilters(prev => ({ ...prev, sortBy: undefined }));
  }, []);

  const toggleFilterModal = useCallback(() => {
    if (isFilterOpen) {
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start(() => setIsFilterOpen(false));
    } else {
      setIsFilterOpen(true);
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  }, [isFilterOpen]);

  const handleOpportunityPress = useCallback(
    (id: string) => {
      const opportunitySource = viewMode === "saved" ? savedOpportunities : allOpportunities;
      const opportunity = opportunitySource.find((opp) => opp.id === id);
      if (opportunity) {
        setSelectedOpportunity(opportunity);
        showDetailModal();

        // Track semantic search click if AI search is enabled
        if (isAISearchEnabled && debouncedSearchQuery.trim()) {
          trackSemanticSearch(debouncedSearchQuery, filteredOpportunities.length, id);
        }
      }
    },
    [allOpportunities, savedOpportunities, viewMode, isAISearchEnabled, debouncedSearchQuery, filteredOpportunities.length],
  );

  const showDetailModal = () => {
    if (Platform.OS === "web") {
      document.body.style.overflow = "hidden";
    }
    setDetailModalVisible(true);
    Animated.parallel([
      Animated.timing(backdropOpacity, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.spring(detailSlideAnim, {
        toValue: 0,
        tension: 40,
        friction: 8,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const hideDetailModal = () => {
    // Start the hide animation
    Animated.parallel([
      Animated.timing(backdropOpacity, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(detailSlideAnim, {
        toValue: height,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start(() => {
      // After animation completes, clean up
      setDetailModalVisible(false);
      setSelectedOpportunity(null);

      // Force reset animation values for mobile touch fix
      if (Platform.OS !== "web") {
        backdropOpacity.setValue(0);
        detailSlideAnim.setValue(height);

        // Force re-render the grid to reset touch handling
        setForceUpdate((prev) => prev + 1);

        // Small delay to ensure modal is fully cleaned up
        setTimeout(() => {
          // Additional cleanup to prevent touch conflicts
          backdropOpacity.setValue(0);
          detailSlideAnim.setValue(height);

          // Reset ScrollView to ensure proper touch handling
          if (mainScrollViewRef.current) {
            mainScrollViewRef.current.scrollTo({
              y: mainScrollViewRef.current.scrollY || 0,
              animated: false,
            });
          }
        }, 50);
      }
    });

    // Restore scrolling for web immediately
    if (Platform.OS === "web") {
      document.body.style.overflow = "";
    }
  };

  const handleCloseDetail = () => hideDetailModal();

  // Render item function for FlatList - memoized for performance
  const renderOpportunityItem = useCallback(
    ({ item, index }: { item: Opportunity; index: number }) => (
      <OpportunityCard
        opportunity={item}
        onPress={handleOpportunityPress}
        index={index}
        data-oid="9e:7sg2"
      />
    ),

    [handleOpportunityPress],
  );

  const renderGridView = useCallback(() => {
    // Split opportunities into regular and featured
    const regularOpportunities = filteredOpportunities.filter(
      (opp) => !(opp as any).isFeatured,
    );
    const featuredOpportunity = filteredOpportunities.find(
      (opp) => (opp as any).isFeatured,
    );

    return (
      <View style={styles.gridContainer} data-oid="7vo0:v7">
        {/* Featured Opportunity */}
        {featuredOpportunity && (
          <View style={styles.featuredSection} data-oid="c:i.mtc">
            <FeaturedOpportunity
              opportunity={featuredOpportunity}
              onPress={handleOpportunityPress}
              data-oid=".13add_"
            />
          </View>
        )}

        {/* Regular Opportunities Grid - Virtualized with FlatList */}
        {regularOpportunities.length > 0 ? (
          <FlatList
            key={`grid-${forceUpdate}`}
            data={regularOpportunities}
            renderItem={renderOpportunityItem}
            keyExtractor={(item) => item.id}
            numColumns={numColumns > 1 ? numColumns : 1}
            columnWrapperStyle={numColumns > 1 ? styles.gridWrapper : undefined}
            contentContainerStyle={styles.flatListContainer}
            showsVerticalScrollIndicator={false}
            removeClippedSubviews={true}
            maxToRenderPerBatch={10}
            windowSize={10}
            initialNumToRender={8}
            updateCellsBatchingPeriod={50}
            scrollEventThrottle={16}
            getItemLayout={
              numColumns === 1
                ? (data, index) => ({
                  length: 280, // Approximate card height
                  offset: 280 * index,
                  index,
                })
                : undefined
            }
            data-oid="-8mc_pb"
          />
        ) : !featuredOpportunity ? (
          <View style={styles.emptyContainer} data-oid="0nlehr-">
            <Ionicons
              name={viewMode === "saved" ? "bookmark-outline" : "search-outline"}
              size={48}
              color="#A1A1AA"
              data-oid="0yt_yxb"
            />

            <Text style={styles.emptyText} data-oid="yqdxten">
              {viewMode === "saved"
                ? "No saved opportunities yet"
                : "No opportunities found"}
            </Text>
            <Text style={styles.emptySubText} data-oid="q8vx912">
              {viewMode === "saved"
                ? "Start exploring and save opportunities that interest you"
                : "Try adjusting your search or filters"}
            </Text>
          </View>
        ) : null}
      </View>
    );
  }, [filteredOpportunities, forceUpdate, numColumns, renderOpportunityItem]);

  // Function to set up home page SEO
  const setupHomeSEO = () => {
    // Set document title
    updateDocumentTitle(SITE_CONFIG.siteName);

    // Set meta description
    updateMetaTag("description", SITE_CONFIG.description);

    // Set keywords
    updateMetaTag("keywords", SITE_CONFIG.keywords);

    // Set canonical URL
    updateCanonicalLink(SITE_CONFIG.siteUrl);

    // Update Open Graph tags
    updateMetaTag("og:title", SITE_CONFIG.siteName, true);
    updateMetaTag("og:description", SITE_CONFIG.description, true);
    updateMetaTag("og:url", SITE_CONFIG.siteUrl, true);
    updateMetaTag("og:type", "website", true);
    updateMetaTag("og:image", `${SITE_CONFIG.siteUrl}/og-image.png`, true);
    updateMetaTag("og:locale", SITE_CONFIG.locale, true);

    // Update Twitter Card tags
    // updateMetaTag("twitter:title", SITE_CONFIG.siteName);
    // updateMetaTag("twitter:description", SITE_CONFIG.description);
    // updateMetaTag("twitter:image", `${SITE_CONFIG.siteUrl}/og-image.png`);

    // Set up main site structured data
    const websiteStructuredData = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_CONFIG.siteName,
      url: SITE_CONFIG.siteUrl,
      description: SITE_CONFIG.description,
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${SITE_CONFIG.siteUrl}/?search={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
      publisher: {
        "@type": "Organization",
        name: SITE_CONFIG.siteName,
        url: SITE_CONFIG.siteUrl,
        logo: `${SITE_CONFIG.siteUrl}/icon-512.png`,
      },
    };

    updateStructuredData(websiteStructuredData, "website-structured-data");

    // Set up organization structured data
    const organizationStructuredData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_CONFIG.siteName,
      url: SITE_CONFIG.siteUrl,
      description:
        "A centralized platform for discovering STEM opportunities across British Columbia, connecting students, professionals, and educators with competitions, grants, scholarships, and more.",
      logo: `${SITE_CONFIG.siteUrl}/icon-512.png`,
      foundingDate: "2024",
      founder: {
        "@type": "Person",
        name: SITE_CONFIG.creator,
      },
      areaServed: {
        "@type": "Place",
        name: "British Columbia, Canada",
      },
      knowsAbout: [
        "STEM Education",
        "Academic Competitions",
        "Research Grants",
        "Educational Scholarships",
        "Technology Hackathons",
        "Scientific Conferences",
        "British Columbia Education",
      ],

      sameAs: [
        "https://instagram.com/bcstem.ca",
        //"https://twitter.com/bcstemopp",
      ],
    };

    updateStructuredData(
      organizationStructuredData,
      "organization-structured-data",
    );
  };

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: themed.background }]}
      data-oid="e0rhw2f"
    >
      <StatusBar
        style={theme === "dark" ? "light" : "dark"}
        data-oid="9:w46dx"
      />

      <View style={styles.contentWrapper} data-oid="aej.n24">
        <Header
          onInfoPress={() => navigation.navigate("About")}
          onAdminPress={() => setIsAdminOpen(true)}
          backgroundColor={themed.card}
          data-oid="9xtgr3:"
        />

        {/* Milestone Banner */}
        {/* {showMilestoneBanner && (
          <MilestoneBanner
            onDismiss={() => setShowMilestoneBanner(false)}
          />
        )} */}

        {/* Team Banner temporarily hidden
        {showTeamBanner && (
          <TeamBanner
            onDismiss={() => setShowTeamBanner(false)}
          />
        )}
        */}

        {/* Canada Day Banner */}
        {/* {showCanadaDayBanner && (
          <View 
            style={[
              styles.canadaDayBanner, 
              { backgroundColor: themed.canadaDayBg, borderBottomColor: themed.canadaDayBorder }
            ]}
          >
            <View style={styles.canadaDayContent}>
              <View style={styles.canadaDayTextContainer}>
                <Ionicons 
                  name="leaf" 
                  size={isSmallMobile ? 16 : 18} 
                  color="#DC2626" 
                  style={styles.canadaDayIcon}
                />
                <Text style={[styles.canadaDayText, { color: themed.text }]}>
                  Happy Canada Day! 🇨🇦
                </Text>
              </View>
              <TouchableOpacity
                style={styles.canadaDayCloseButton}
                onPress={() => setShowCanadaDayBanner(false)}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons 
                  name="close" 
                  size={isSmallMobile ? 16 : 18} 
                  color={themed.subText}
                />
              </TouchableOpacity>
            </View>
          </View>
        )} */}

        <Animated.View
          style={[
            // @ts-expect-error: headerShadow may not be defined in styles, but is used for shadow effect
            styles.headerShadow,
            { opacity: headerOpacity, backgroundColor: themed.background },
          ]}
          data-oid="2thje:3"
        />

        <Animated.ScrollView
          style={styles.mainContent}
          showsVerticalScrollIndicator={false}
          scrollEventThrottle={16}
          ref={mainScrollViewRef}
          onScroll={Animated.event(
            [{ nativeEvent: { contentOffset: { y: scrollY } } }],
            { useNativeDriver: false },
          )}
          data-oid="n1vqsri"
        >
          <View
            style={[styles.heroSection, { backgroundColor: themed.card }]}
            data-oid="jx:7arg"
          >
            <View style={styles.heroContent} data-oid="0kcycbo">
              <Text
                accessibilityRole="header"
                aria-level={1}
                style={[styles.heroTitle, { color: themed.text }]}
                data-oid="ko2l:dn"
              >
                Discover STEM Opportunities in BC
              </Text>
              <Text
                style={[styles.heroSubtitle, { color: themed.subText }]}
                data-oid="m:w_no9"
              >
                Your centralized platform for competitions, grants,
                scholarships, and more
              </Text>
            </View>
            <View style={styles.searchSection}>
              <SearchBar
                value={searchQuery}
                onChangeText={(text) => {
                  console.log('🔍 Search input changed:', text);
                  setSearchQuery(text);
                }}
                onSubmit={() => {
                  trackSearch(searchQuery);
                  if (isAISearchEnabled && searchQuery.trim()) {
                    performSearch(searchQuery);
                  }
                }}
                isAISearchEnabled={isAISearchEnabled}
                onAISearchToggle={setIsAISearchEnabled}
                onFilterPress={toggleFilterModal}
                isFilterActive={isFilterOpen}
              />
            </View>
            <ActiveFilters
              filters={{
                type: selectedTypes,
                status: selectedStatuses,
                gradeLevel: selectedGradeLevels,
                interactionType: selectedInteractionTypes,
              }}
              onRemoveType={removeTypeFilter}
              onRemoveStatus={removeStatusFilter}
              onRemoveGradeLevel={removeGradeLevelFilter}
              onRemoveInteractionType={removeInteractionTypeFilter}
              onClearAll={clearFilters}
              data-oid="10.y-sp"
            />

            {isFilterOpen && (
              <Animated.View
                style={[
                  styles.filterPanel,
                  {
                    opacity: fadeAnim,
                    transform: [
                      {
                        translateY: fadeAnim.interpolate({
                          inputRange: [0, 1],
                          outputRange: [-20, 0],
                        }),
                      },
                    ],
                  },
                ]}
                data-oid="jqd8w1t"
              >
                <View
                  style={[
                    styles.filterPanelInner,
                    {
                      backgroundColor: themed.filterPanel,
                      borderColor: themed.filterPanelBorder,
                    },
                  ]}
                  data-oid="ig1:z69"
                >
                  <View style={styles.filterPanelHeader} data-oid="2f:t..7">
                    <Text
                      style={[styles.filterPanelTitle, { color: themed.text }]}
                      data-oid="i1px--7"
                    >
                      Filter Opportunities
                    </Text>
                    {(selectedTypes.length > 0 ||
                      selectedGradeLevels.length > 0 ||
                      selectedStatuses.length > 0 ||
                      selectedInteractionTypes.length > 0) && (
                        <TouchableOpacity
                          onPress={clearFilters}
                          style={styles.clearFiltersButton}
                          data-oid="1k24r-z"
                        >
                          <Text
                            style={[
                              styles.clearFiltersText,
                              { color: themed.text },
                            ]}
                            data-oid="37eniea"
                          >
                            Clear All
                          </Text>
                        </TouchableOpacity>
                      )}
                  </View>
                  <View style={styles.filterSection} data-oid="9pce300">
                    <Text
                      style={[
                        styles.filterSectionTitle,
                        { color: themed.text },
                      ]}
                      data-oid="z_8-nma"
                    >
                      Opportunity Type
                    </Text>
                    <View
                      style={styles.filterChipsContainer}
                      data-oid="mmplhw4"
                    >
                      {opportunityTypes.map((type) => (
                        <TouchableOpacity
                          key={type}
                          style={[
                            styles.filterChip,
                            {
                              backgroundColor: themed.chip,
                              borderColor: themed.inputBorder,
                            },
                            selectedTypes.includes(type) && {
                              backgroundColor: themed.chipActive,
                              borderColor: themed.chipActive,
                            },
                          ]}
                          onPress={() => toggleFilterType(type)}
                          data-oid="ng1s7i0"
                        >
                          <Text
                            style={[
                              styles.filterChipText,
                              { color: themed.chipText },
                              selectedTypes.includes(type) && {
                                color: themed.chipActiveText,
                              },
                            ]}
                            data-oid="i-xf38i"
                          >
                            {type}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>

                  <View style={styles.filterSection} data-oid="43_qxn3">
                    <Text
                      style={[
                        styles.filterSectionTitle,
                        { color: themed.text },
                      ]}
                      data-oid="g4x97kn"
                    >
                      Grade Level
                    </Text>
                    <View
                      style={styles.filterChipsContainer}
                      data-oid="ef5b2u."
                    >
                      {gradeLevels.map((level) => (
                        <TouchableOpacity
                          key={level}
                          style={[
                            styles.filterChip,
                            {
                              backgroundColor: themed.chip,
                              borderColor: themed.inputBorder,
                            },
                            selectedGradeLevels.includes(level) && {
                              backgroundColor: themed.chipActive,
                              borderColor: themed.chipActive,
                            },
                          ]}
                          onPress={() => toggleGradeLevel(level)}
                          data-oid="bvcszcn"
                        >
                          <Text
                            style={[
                              styles.filterChipText,
                              { color: themed.chipText },
                              selectedGradeLevels.includes(level) && {
                                color: themed.chipActiveText,
                              },
                            ]}
                            data-oid="9tkfnwv"
                          >
                            {level}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>

                  <View style={styles.filterSection} data-oid="pwmql_g">
                    <Text
                      style={[
                        styles.filterSectionTitle,
                        { color: themed.text },
                      ]}
                      data-oid="6s6d3d2"
                    >
                      Status
                    </Text>
                    <View
                      style={styles.filterChipsContainer}
                      data-oid="oxhyxaq"
                    >
                      {(
                        [
                          "Open",
                          "Coming Soon",
                          "Ongoing",
                          "Closed",
                        ] as OpportunityStatus[]
                      ).map((status) => (
                        <TouchableOpacity
                          key={status}
                          style={[
                            styles.filterChip,
                            {
                              backgroundColor: themed.chip,
                              borderColor: themed.inputBorder,
                            },
                            selectedStatuses.includes(status) && {
                              backgroundColor: themed.chipActive,
                              borderColor: themed.chipActive,
                            },
                          ]}
                          onPress={() => toggleStatusFilter(status)}
                          data-oid="-jxy5pn"
                        >
                          <Ionicons
                            name={
                              status === "Open"
                                ? "checkmark-circle-outline"
                                : status === "Coming Soon"
                                  ? "time-outline"
                                  : status === "Ongoing"
                                    ? "hourglass-outline"
                                    : "close-circle-outline"
                            }
                            size={16}
                            color={
                              selectedStatuses.includes(status)
                                ? themed.chipActiveText
                                : themed.chipText
                            }
                            style={{ marginRight: 6 }}
                            data-oid="371vcun"
                          />

                          <Text
                            style={[
                              styles.filterChipText,
                              { color: themed.chipText },
                              selectedStatuses.includes(status) && {
                                color: themed.chipActiveText,
                              },
                            ]}
                            data-oid="8vyxw3c"
                          >
                            {status}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>

                  <View style={styles.filterSection} data-oid="interact-filter">
                    <Text
                      style={[
                        styles.filterSectionTitle,
                        { color: themed.text },
                      ]}
                      data-oid="interact-title"
                    >
                      Format
                    </Text>
                    <View
                      style={styles.filterChipsContainer}
                      data-oid="interact-chips"
                    >
                      {interactionTypes.map((type) => (
                        <TouchableOpacity
                          key={type}
                          style={[
                            styles.filterChip,
                            {
                              backgroundColor: themed.chip,
                              borderColor: themed.inputBorder,
                            },
                            selectedInteractionTypes.includes(type) && {
                              backgroundColor: themed.chipActive,
                              borderColor: themed.chipActive,
                            },
                          ]}
                          onPress={() => toggleInteractionTypeFilter(type)}
                          data-oid={`interact-${type}`}
                        >
                          <Ionicons
                            name={
                              type === "In-Person"
                                ? "people-outline"
                                : type === "Virtual"
                                  ? "laptop-outline"
                                  : "globe-outline"
                            }
                            size={16}
                            color={
                              selectedInteractionTypes.includes(type)
                                ? themed.chipActiveText
                                : themed.chipText
                            }
                            style={{ marginRight: 6 }}
                            data-oid={`interact-icon-${type}`}
                          />
                          <Text
                            style={[
                              styles.filterChipText,
                              { color: themed.chipText },
                              selectedInteractionTypes.includes(type) && {
                                color: themed.chipActiveText,
                              },
                            ]}
                            data-oid={`interact-text-${type}`}
                          >
                            {type}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>

                  <View style={styles.filterSection} data-oid="03c57x_">
                    <Text
                      style={[
                        styles.filterSectionTitle,
                        { color: themed.text },
                      ]}
                      data-oid="ky3rb_y"
                    >
                      Sort By
                    </Text>
                    <View
                      style={styles.filterChipsContainer}
                      data-oid="0f.53gv"
                    >
                      {[
                        {
                          key: "deadline-asc",
                          label: "Deadline (Soonest)",
                          icon: "time-outline",
                        },
                        {
                          key: "date-asc",
                          label: "Date (Soonest)",
                          icon: "calendar-outline",
                        },
                        {
                          key: "alpha",
                          label: "Alphabetical",
                          icon: "text-outline",
                        },
                      ].map((sortOption) => (
                        <TouchableOpacity
                          key={sortOption.key}
                          style={[
                            styles.filterChip,
                            {
                              backgroundColor: themed.chip,
                              borderColor: themed.inputBorder,
                            },
                            filters.sortBy === sortOption.key && {
                              backgroundColor: themed.chipActive,
                              borderColor: themed.chipActive,
                            },
                          ]}
                          onPress={() =>
                            setFilters({
                              ...filters,
                              sortBy: sortOption.key as any,
                            })
                          }
                          data-oid="r-j0j9w"
                        >
                          <Ionicons
                            name={sortOption.icon as any}
                            size={16}
                            color={
                              filters.sortBy === sortOption.key
                                ? themed.chipActiveText
                                : themed.chipText
                            }
                            style={{ marginRight: 6 }}
                            data-oid="03j8nib"
                          />

                          <Text
                            style={[
                              styles.filterChipText,
                              { color: themed.chipText },
                              filters.sortBy === sortOption.key && {
                                color: themed.chipActiveText,
                              },
                            ]}
                            data-oid="z:ymit_"
                          >
                            {sortOption.label}
                          </Text>
                        </TouchableOpacity>
                      ))}

                      {/* Clear Sort Option */}
                      {filters.sortBy && (
                        <TouchableOpacity
                          style={[
                            styles.filterChip,
                            {
                              backgroundColor: themed.chip,
                              borderColor: themed.inputBorder,
                            },
                          ]}
                          onPress={() =>
                            setFilters({
                              ...filters,
                              sortBy: undefined,
                            })
                          }
                        >
                          <Ionicons
                            name="close-circle-outline"
                            size={16}
                            color={themed.chipText}
                            style={{ marginRight: 6 }}
                          />
                          <Text
                            style={[
                              styles.filterChipText,
                              { color: themed.chipText },
                            ]}
                          >
                            Clear Sort
                          </Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  </View>
                </View>
              </Animated.View>
            )}
          </View>
          <View style={[styles.contentSection]} data-oid="7z-:fyt">
            <View style={styles.contentHeader} data-oid="zcj-8mz">
              <View
                style={[
                  styles.resultInfoBar,
                  {
                    backgroundColor: themed.resultBar,
                    borderColor: themed.resultBarBorder,
                  },
                ]}
                data-oid="l-ylb61"
              >
                <View style={styles.resultCountSection} data-oid="zvfnnt1">
                  {isAISearchEnabled && debouncedSearchQuery.trim() && (
                    <View style={styles.aiSearchIndicator}>
                      <LinearGradient
                        colors={["#272727", "#52525B"]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={styles.aiSearchBadge}
                      >
                        <Ionicons name="sparkles" size={12} color="#FFFFFF" />
                        <Text style={styles.aiSearchBadgeText}>AI</Text>
                      </LinearGradient>
                    </View>
                  )}
                  <Text
                    style={[styles.resultCount, { color: themed.text }]}
                    data-oid="vxi3.69"
                  >
                    {viewMode === "saved" ? (
                      <>
                        {filteredOpportunities.length}{" "}
                        {filteredOpportunities.length === 1
                          ? "saved opportunity"
                          : "saved opportunities"}
                      </>
                    ) : (
                      <>
                        {filteredOpportunities.length}{" "}
                        {filteredOpportunities.length === 1
                          ? "opportunity"
                          : "opportunities"}{" "}
                        found
                      </>
                    )}
                  </Text>
                  {!isSmallMobile && (
                    <TouchableOpacity
                      style={styles.infoButtonCompact}
                      onPress={() => navigation.navigate("About")}
                      data-oid="6l:wdld"
                    >
                      <Ionicons
                        name="information-circle-outline"
                        size={14}
                        color={themed.iconInactive}
                        data-oid="iu5vvi-"
                      />
                    </TouchableOpacity>
                  )}
                </View>
                <View style={styles.controlsSection} data-oid="c6c-n19">
                  <View
                    style={[
                      styles.viewToggleContainer,
                      {
                        backgroundColor: themed.inputBg,
                        borderColor: themed.inputBorder,
                      },
                    ]}
                    data-oid="xi-1.6h"
                  >
                    <TouchableOpacity
                      style={[
                        styles.viewToggleButton,
                        viewMode === "grid" && styles.activeViewToggle,
                        viewMode === "grid" && { backgroundColor: themed.card },
                      ]}
                      onPress={() => {
                        if (viewMode !== "grid") {
                          setViewMode("grid");
                        }
                      }}
                      data-oid="z:5dyma"
                    >
                      <Ionicons
                        name="grid-outline"
                        size={isSmallMobile ? 18 : 20}
                        color={
                          viewMode === "grid"
                            ? themed.iconActive
                            : themed.iconInactive
                        }
                        data-oid=".g-2wca"
                      />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.viewToggleButton,
                        viewMode === "saved" && styles.activeViewToggle,
                        viewMode === "saved" && {
                          backgroundColor: themed.card,
                        },
                      ]}
                      onPress={() => {
                        if (viewMode !== "saved") {
                          if (!isAuthenticated) {
                            setShowAuthRequiredModal(true);
                          } else {
                            setViewMode("saved");
                            refreshSaved(); // Refresh saved opportunities when switching to saved view
                          }
                        }
                      }}
                      data-oid="saved-toggle"
                    >
                      <Ionicons
                        name="bookmark"
                        size={isSmallMobile ? 18 : 20}
                        color={
                          viewMode === "saved"
                            ? themed.iconActive
                            : themed.iconInactive
                        }
                        data-oid="saved-icon"
                      />
                      {isAuthenticated && savedOpportunities.length > 0 && (
                        <Text
                          style={[
                            styles.viewToggleCount,
                            {
                              color: viewMode === "saved"
                                ? themed.iconActive
                                : themed.iconInactive,
                            },
                          ]}
                        >
                          {savedOpportunities.length}
                        </Text>
                      )}
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.viewToggleButton,
                        viewMode === "calendar" && styles.activeViewToggle,
                        viewMode === "calendar" && {
                          backgroundColor: themed.card,
                        },
                      ]}
                      onPress={() => {
                        if (viewMode !== "calendar") {
                          setViewMode("calendar");
                        }
                      }}
                      data-oid="6qrxn8r"
                    >
                      <Ionicons
                        name="calendar-outline"
                        size={isSmallMobile ? 18 : 20}
                        color={
                          viewMode === "calendar"
                            ? themed.iconActive
                            : themed.iconInactive
                        }
                        data-oid="6u329:l"
                      />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            </View>
            {isLoading ? (
              <View style={styles.loadingContainer} data-oid="ibyfcn8">
                <ActivityIndicator
                  size="large"
                  color={themed.iconActive}
                  data-oid="686-mjf"
                />

                <Text
                  style={[styles.loadingText, { color: themed.text }]}
                  data-oid="oyj-lat"
                >
                  Loading opportunities...
                </Text>
              </View>
            ) : loadError ? (
              <View style={styles.errorContainer} data-oid="rudjbhs">
                <Ionicons
                  name="alert-circle-outline"
                  size={40}
                  color="#EF4444"
                  data-oid="rayune2"
                />

                <Text style={styles.errorText} data-oid="fv-5xte">
                  {loadError}
                </Text>
                <TouchableOpacity
                  style={[
                    styles.retryButton,
                    { backgroundColor: themed.retryButton },
                  ]}
                  onPress={() => window.location.reload()}
                  data-oid="vrlt66p"
                >
                  <Ionicons
                    name="refresh-outline"
                    size={18}
                    color={themed.retryText}
                    data-oid="_g90hh1"
                  />

                  <Text
                    style={[styles.retryText, { color: themed.retryText }]}
                    data-oid="34onz-3"
                  >
                    Retry
                  </Text>
                </TouchableOpacity>
              </View>
            ) : viewMode === "grid" || viewMode === "saved" ? (
              renderGridView()
            ) : (
              <View
                style={[
                  styles.underConstructionContainer,
                  { backgroundColor: themed.underConstructionBg },
                ]}
                data-oid="d.jawxy"
              >
                <View
                  style={[
                    styles.underConstructionCard,
                    {
                      backgroundColor: themed.underConstructionCard,
                      borderColor: themed.underConstructionCardBorder,
                    },
                  ]}
                  data-oid="e9rj34-"
                >
                  <Animated.View
                    style={{
                      opacity: 1,
                      transform: [
                        {
                          rotate: fadeAnim.interpolate({
                            inputRange: [0, 1],
                            outputRange: ["0deg", "360deg"],
                          }),
                        },
                        {
                          scale: pulseAnim,
                        },
                      ],
                    }}
                    data-oid="_v8o:na"
                  >
                    <Ionicons
                      name="build-outline"
                      size={60}
                      color={themed.underConstructionTitle}
                      data-oid="adon.1d"
                    />
                  </Animated.View>
                  <Animated.View
                    style={{
                      opacity: fadeAnim.interpolate({
                        inputRange: [0, 0.5, 1],
                        outputRange: [0.7, 1, 0.7],
                      }),
                      transform: [
                        {
                          translateY: fadeAnim.interpolate({
                            inputRange: [0, 0.5, 1],
                            outputRange: [0, -5, 0],
                          }),
                        },
                      ],
                    }}
                    data-oid="my.q2mr"
                  >
                    <Text
                      style={[
                        styles.underConstructionTitle,
                        { color: themed.underConstructionTitle },
                      ]}
                      data-oid="pmhw3q7"
                    >
                      Calendar View Under Construction
                    </Text>
                    <Text
                      style={[
                        styles.underConstructionText,
                        { color: themed.underConstructionText },
                      ]}
                      data-oid="yllevmi"
                    >
                      We're enhancing our calendar experience to make it more
                      useful and intuitive. The development team is working hard
                      to bring it back better than ever!
                    </Text>
                    <View
                      style={styles.progressBarContainer}
                      data-oid="ja1wvnv"
                    >
                      <Animated.View
                        style={[
                          styles.progressBar,
                          {
                            backgroundColor: themed.progressBar,
                            width: progressAnim.interpolate({
                              inputRange: [0, 1],
                              outputRange: ["5%", "95%"],
                            }),
                          },
                        ]}
                        data-oid="uubefng"
                      >
                        {/* Shimmer disabled for performance */}
                        {/* <Animated.View 
                        style={[
                        styles.progressGlow,
                        {
                          backgroundColor: themed.progressGlow,
                          opacity: shimmerAnim.interpolate({
                            inputRange: [0, 0.5, 1],
                            outputRange: [0.4, 0.6, 0.4],
                          }),
                          transform: [{
                            translateX: shimmerAnim.interpolate({
                              inputRange: [0, 1],
                              outputRange: [-10, 10],
                            })
                          }]
                        }
                        ]} 
                        /> */}
                      </Animated.View>
                    </View>
                    <Text style={styles.progressText} data-oid="idbb7bl">
                      Development in progress...
                    </Text>
                    <TouchableOpacity
                      style={[
                        styles.returnToGridButton,
                        { backgroundColor: themed.returnToGridButton },
                      ]}
                      onPress={() => setViewMode("grid")}
                      activeOpacity={0.8}
                      data-oid=":-sd9j0"
                    >
                      <Ionicons
                        name="grid-outline"
                        size={20}
                        color={themed.returnToGridText}
                        style={{ marginRight: 8 }}
                        data-oid="psnbzel"
                      />

                      <Text
                        style={[
                          styles.returnToGridText,
                          { color: themed.returnToGridText },
                        ]}
                        data-oid="48:tuyl"
                      >
                        Return to Grid View
                      </Text>
                    </TouchableOpacity>
                  </Animated.View>
                </View>
              </View>
            )}
          </View>
          <Footer />
        </Animated.ScrollView>
      </View>

      {/* Opportunity Detail Modal */}
      {
        detailModalVisible && selectedOpportunity && (
          <Modal
            animationType="none"
            transparent={true}
            visible={detailModalVisible}
            onRequestClose={handleCloseDetail}
            statusBarTranslucent
            data-oid="xnewxgv"
          >
            <Animated.View
              style={[
                styles.modalOverlay,
                {
                  opacity: backdropOpacity,
                  backgroundColor: themed.modalBg, // dark mode support
                },
              ]}
              data-oid="gb4w3ua"
            >
              <Pressable
                style={styles.backdropPressable}
                onPress={handleCloseDetail}
                data-oid="5p2-119"
              />

              <Animated.View
                style={[
                  styles.detailContainer,
                  {
                    backgroundColor: themed.modalCard, // dark mode support
                    transform: [{ translateY: detailSlideAnim }],
                    maxWidth: 900,
                    width: isTabletOrDesktop ? "80%" : "95%",
                    maxHeight: isSmallMobile ? "98%" : "90%",
                  },
                ]}
                data-oid="45uofbp"
              >
                <React.Suspense
                  fallback={
                    <View style={styles.loadingContainer} data-oid="5a5g:62">
                      <ActivityIndicator
                        size="large"
                        color={themed.text || "#272727"}
                        data-oid="7rtxle-"
                      />
                    </View>
                  }
                  data-oid="q4:9q:7"
                >
                  <OpportunityDetail
                    opportunity={selectedOpportunity}
                    onClose={handleCloseDetail}
                    data-oid="70--fb7"
                  />
                </React.Suspense>
              </Animated.View>
            </Animated.View>
          </Modal>
        )
      }

      {/* Floating Feedback Button */}
      <TouchableOpacity
        style={[
          styles.floatingReportButton,
          { backgroundColor: themed.reportButton },
        ]}
        onPress={() => setIsReportFormVisible(true)}
        data-oid="x53rozl"
      >
        <Ionicons
          name="alert-circle-outline"
          size={24}
          color={themed.reportIcon}
          data-oid="i-6306e"
        />
      </TouchableOpacity>

      {/* Feedback Modal */}
      <FeedbackModal
        isVisible={isReportFormVisible}
        onClose={() => setIsReportFormVisible(false)}
        data-oid="77f1np:"
      />

      {/* Full Screen Admin Panel */}
      {
        isAdminOpen && (
          <View style={styles.fullScreenAdminPanel}>
            <AdminPanel
              onClose={() => setIsAdminOpen(false)}
            />
          </View>
        )
      }

      {/* Auth Required Modal */}
      <AuthRequiredModal
        visible={showAuthRequiredModal}
        onClose={() => setShowAuthRequiredModal(false)}
        featureName="saved opportunities"
      />
    </SafeAreaView >
  );
};

// Your full StyleSheet remains unchanged here
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FAFAFA",
  },
  contentWrapper: {
    flex: 1,
    position: "relative",
  },
  mainContent: {
    flex: 1,
  },
  heroSection: {
    backgroundColor: "#FFFFFF",
    paddingTop: isSmallMobile ? 24 : 40,
    paddingBottom: isSmallMobile ? 16 : 24,
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 3,
    zIndex: 5,
  },
  heroContent: {
    paddingHorizontal: isSmallMobile ? 16 : 24,
    marginBottom: isSmallMobile ? 20 : 32,
    maxWidth: 1200,
    alignSelf: "center",
    width: "100%",
  },
  heroTitle: {
    fontSize: isTabletOrDesktop ? 34 : isSmallMobile ? 24 : 28,
    fontWeight: "800",
    color: "#18181B",
    marginBottom: isSmallMobile ? 8 : 12,
    fontFamily: "Inter-ExtraBold",
    letterSpacing: -0.5,
    textAlign: "left",
  },
  heroSubtitle: {
    fontSize: isSmallMobile ? 14 : 16,
    color: "#52525B",
    lineHeight: isSmallMobile ? 20 : 24,
    fontFamily: "Inter-Regular",
  },
  searchFilterContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: isSmallMobile ? 16 : 24,
    gap: isSmallMobile ? 8 : 12,
    maxWidth: 1200,
    alignSelf: "center",
    width: "100%",
  },
  searchContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F4F4F5",
    borderRadius: 12,
    paddingHorizontal: isSmallMobile ? 12 : 16,
    paddingVertical: isSmallMobile ? 10 : 12,
    borderWidth: 1,
    borderColor: "#E4E4E7",
    gap: isSmallMobile ? 8 : 12,
  },
  searchBarWrapper: {
    flex: 1,
  },
  searchSection: {
    paddingHorizontal: isSmallMobile ? 16 : 24,
    paddingVertical: 12,
    maxWidth: 1200,
    alignSelf: "center",
    width: "100%",
  },
  filterButtonContainer: {
    marginTop: 16,
    alignItems: "flex-start",
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: "500",
    marginLeft: 6,
    fontFamily: "Inter-Medium",
  },
  searchInput: {
    flex: 1,
    fontSize: isSmallMobile ? 14 : 16,
    color: "#27272A",
    fontFamily: "Inter-Regular",
  },
  filterButton: {
    backgroundColor: "#F4F4F5",
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: isSmallMobile ? 10 : 12,
    borderWidth: 1,
    borderColor: "#E4E4E7",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  filterButtonActive: {
    backgroundColor: "#E4E4E7",
    borderColor: "#D4D4D8",
  },
  filterPanel: {
    marginTop: 12,
    paddingHorizontal: isSmallMobile ? 16 : 24,
    maxWidth: 1200,
    alignSelf: "center",
    width: "100%",
  },
  filterPanelMobile: {
    marginHorizontal: 16,
    marginTop: 12,
    position: "absolute",
    top: 120, // Position it below search
    left: 0,
    right: 0,
    zIndex: 20,
  },
  filterPanelInner: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: isSmallMobile ? 16 : 18,
    borderWidth: 1,
    borderColor: "#E4E4E7",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  filterPanelInnerMobile: {
    padding: 16,
    maxHeight: 450,
  },
  filterPanelHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  filterPanelTitle: {
    fontSize: isSmallMobile ? 16 : 18,
    fontWeight: "600",
    color: "#27272A",
    fontFamily: "Inter-SemiBold",
  },
  clearFiltersButton: {
    padding: 8,
  },
  clearFiltersText: {
    color: "#272727",
    fontSize: isSmallMobile ? 13 : 14,
    fontFamily: "Inter-Medium",
  },
  filterScrollContainer: {
    paddingRight: 20, // Add padding for scroll spacing
  },
  filterChipsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 6,
  },
  filterChipsContainerMobile: {
    flexWrap: "nowrap", // Prevent wrapping in scroll view
    marginBottom: 4,
  },
  filterChip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: "#F4F4F5",
    borderRadius: 30,
    borderWidth: 1,
    borderColor: "#E4E4E7",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 6,
  },
  filterChipSmall: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 6,
  },
  activeFilterChip: {
    backgroundColor: "#272727",
    borderColor: "#272727",
  },
  filterChipText: {
    fontSize: 14,
    color: "#52525B",
    fontFamily: "Inter-Medium",
  },
  filterChipTextSmall: {
    fontSize: 12,
  },
  activeFilterChipText: {
    color: "#FFFFFF",
    fontWeight: "500",
  },
  filterSection: {
    marginBottom: 12,
  },
  filterSectionTitle: {
    fontSize: isSmallMobile ? 14 : 16,
    color: "#3F3F46",
    fontWeight: "600",
    marginBottom: 10,
    fontFamily: "Inter-SemiBold",
  },
  doneFilterButton: {
    backgroundColor: "#272727",
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 8,
  },
  doneFilterButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  contentSection: {
    flex: 1,
    paddingHorizontal: isSmallMobile ? 16 : 24,
    paddingTop: isSmallMobile ? 16 : 24,
    paddingBottom: isSmallMobile ? 24 : 40,
    maxWidth: 1200,
    alignSelf: "center",
    width: "100%",
  },
  contentHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  resultInfoBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#F9F9F9",
    borderRadius: 8,
    padding: isSmallMobile ? 10 : 12,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#EEEEEE",
  },
  resultCountSection: {
    flexDirection: "row",
    alignItems: "center",
  },
  aiSearchIndicator: {
    marginRight: 8,
  },
  aiSearchBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    gap: 4,
  },
  aiSearchBadgeText: {
    color: "#FFFFFF",
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.5,
  },
  controlsSection: {
    flexDirection: "row",
    alignItems: "center",
  },
  resultCount: {
    fontSize: isSmallMobile ? 13 : 15,
    color: "#52525B",
    fontFamily: "Inter-Medium",
  },
  infoButtonCompact: {
    padding: 6,
    marginLeft: 6,
  },
  aboutButton: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    paddingVertical: 4,
  },
  aboutButtonText: {
    fontSize: 13,
    color: "#71717A",
    marginLeft: 6,
    fontFamily: "Inter-Medium",
    textDecorationLine: "underline",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 60,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: "#71717A",
    fontFamily: "Inter-Regular",
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 60,
  },
  errorText: {
    marginTop: 16,
    marginBottom: 24,
    fontSize: 16,
    color: "#EF4444",
    fontFamily: "Inter-Regular",
    textAlign: "center",
    maxWidth: "80%",
  },
  retryButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#272727",
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  retryText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    marginLeft: 8,
    fontFamily: "Inter-SemiBold",
  },
  viewToggleContainer: {
    flexDirection: "row",
    backgroundColor: "#F4F4F5",
    borderRadius: 8,
    padding: 4,
    borderWidth: 1,
    borderColor: "#E4E4E7",
  },
  viewToggleButton: {
    paddingHorizontal: isSmallMobile ? 8 : 12,
    paddingVertical: isSmallMobile ? 6 : 8,
    borderRadius: 6,
    flexDirection: "row",
    alignItems: "center",
  },
  activeViewToggle: {
    backgroundColor: "#FFFFFF",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.03,
    shadowRadius: 1,
    elevation: 1,
  },
  viewToggleCount: {
    fontSize: 10,
    marginLeft: 4,
    fontFamily: "Inter-Medium",
    fontWeight: "600",
  },
  gridContent: {
    paddingBottom: 20,
  },
  columnWrapper: {
    justifyContent: "flex-start",
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: isSmallMobile ? 50 : 80,
  },
  emptyText: {
    fontSize: isSmallMobile ? 16 : 18,
    color: "#3F3F46",
    marginTop: 16,
    marginBottom: 8,
    fontFamily: "Inter-SemiBold",
    textAlign: "center",
  },
  emptySubText: {
    fontSize: isSmallMobile ? 13 : 14,
    color: "#71717A",
    fontFamily: "Inter-Regular",
    textAlign: "center",
  },
  // New modal styles
  modalOverlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    padding: isSmallMobile ? 10 : 20,
  },
  backdropPressable: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  detailContainer: {
    backgroundColor: "#ffffff",
    borderRadius: 16,
    overflow: "hidden",
    maxHeight: isSmallMobile ? "95%" : "90%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 5 },
    shadowOpacity: 0.15,
    shadowRadius: 10,
    elevation: 8,
  },
  adminModalContainer: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  adminModalContent: {
    width: isTabletOrDesktop ? "80%" : "95%",
    maxHeight: "90%",
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 6,
  },
  fullScreenAdminPanel: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#F7FAFC",
    zIndex: 1000,
  },

  floatingReportButton: {
    position: "absolute",
    bottom: isSmallMobile ? 24 : 20,
    right: isSmallMobile ? 24 : 20,
    padding: isSmallMobile ? 10 : 14,
    borderRadius: 30,
    backgroundColor: "#272727",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 3,
    elevation: 3,
    zIndex: 100,
    ...(Platform.OS === "web" && {
      transition: "background-color 0.2s ease",
      ":hover": {
        backgroundColor: "#18181B",
      },
    }),
  },
  underConstructionContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    backgroundColor: "#F9FAFB",
  },
  underConstructionCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 32,
    alignItems: "center",
    maxWidth: 500,
    width: "100%",
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    borderWidth: 1,
    borderColor: "#E5E5E5",
  },
  underConstructionTitle: {
    fontSize: 24,
    fontWeight: "700",
    color: "#000000",
    marginTop: 24,
    marginBottom: 12,
    fontFamily: "Inter-Bold",
    textAlign: "center",
  },
  underConstructionText: {
    fontSize: 16,
    color: "#4A5568",
    marginBottom: 18,
    fontFamily: "Inter-Regular",
    textAlign: "center",
    lineHeight: 24,
  },
  progressBarContainer: {
    width: "100%",
    height: 8,
    backgroundColor: "#E2E8F0",
    borderRadius: 4,
    marginBottom: 10,
    overflow: "hidden",
  },
  progressBar: {
    height: "100%",
    backgroundColor: "#000000",
    borderRadius: 4,
    position: "relative",
    opacity: 1,
  },
  progressGlow: {
    position: "absolute",
    right: 0,
    top: 0,
    width: 20,
    height: "100%",
    backgroundColor: "#FFFFFF",
    opacity: 0.5,
    borderRadius: 4,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.4,
    shadowRadius: 2,
  },
  progressText: {
    fontSize: 14,
    color: "#718096",
    marginBottom: 28,
    fontFamily: "Inter-Medium",
    textAlign: "center",
  },
  returnToGridButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#000000",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 3,
    opacity: 1,
  },
  returnToGridText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  gridContainer: {
    flex: 1,
  },
  gridWrapper: {
    flexDirection: "row",
    flexWrap: "wrap",
    paddingBottom: 20,
    justifyContent: "flex-start",
  },
  featuredSection: {
    marginBottom: 16,
    paddingHorizontal: 8,
  },
  featuredGridContainer: {
    width: "100%",
    marginBottom: 16,
  },
  flatListContainer: {
    paddingBottom: 20,
  },
  // Canada Day Banner Styles
  canadaDayBanner: {
    paddingVertical: isSmallMobile ? 10 : 12,
    borderBottomWidth: 1,
    zIndex: 10,
    alignItems: "center",
  },
  canadaDayContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    maxWidth: 1200,
    width: "100%",
    paddingHorizontal: isSmallMobile ? 40 : 50,
  },
  canadaDayTextContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  canadaDayIcon: {
    marginRight: 8,
  },
  canadaDayText: {
    fontSize: isSmallMobile ? 14 : 15,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  canadaDayCloseButton: {
    position: "absolute",
    right: isSmallMobile ? 16 : 24,
    top: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    padding: 4,
  },
});

export default HomeScreen;
